/**
 * BaseMap – enveloppe partagée autour de `<Map>` de @vis.gl/react-maplibre.
 *
 * Centralise le style PMTiles, le contrôle de navigation et l'attribution.
 */
import { forwardRef, useEffect, useMemo, type ComponentProps, type ReactNode } from 'react'
import {
  Map as MaplibreMap,
  NavigationControl,
  AttributionControl,
  useMap,
  type MapRef,
} from '@vis.gl/react-maplibre'
import 'maplibre-gl/dist/maplibre-gl.css'
import { useAppColorScheme } from '../hooks/useAppColorScheme'
import {
  resolveBasemapStyleUrl,
  type BasemapContext,
} from '../utils/basemapStyle'
import type { MapBackground } from '../utils/mapTheme'
import { MAP_MAX_BOUNDS, MAP_MIN_ZOOM } from '../utils/mapStyle'

type MaplibreMapProps = ComponentProps<typeof MaplibreMap>
type AttributionPosition = ComponentProps<typeof AttributionControl>['position']

/** Ferme l'attribution au chargement (MapLibre part souvent avec open + compact-show). */
function CollapseAttributionOnLoad({ enabled }: { enabled: boolean }) {
  const { current: map } = useMap()
  useEffect(() => {
    if (!enabled || !map) return
    const collapse = () => {
      const el = map.getContainer().querySelector('.maplibregl-ctrl-attrib')
      if (!(el instanceof HTMLElement)) return
      el.classList.add('maplibregl-compact')
      el.classList.remove('maplibregl-compact-show')
      if (el instanceof HTMLDetailsElement) el.open = false
      else el.removeAttribute('open')
    }
    collapse()
    map.on('load', collapse)
    const t = window.setTimeout(collapse, 300)
    return () => {
      map.off('load', collapse)
      window.clearTimeout(t)
    }
  }, [enabled, map])
  return null
}

export interface BaseMapProps extends Omit<MaplibreMapProps, 'attributionControl'> {
  /** Contexte d'affichage : flat (défaut) ou topo (/map, plein écran fiche). */
  basemapContext?: BasemapContext
  /** Fond choisi sur /map (topo ou satellite) — ignoré hors contexte map. */
  mapBackground?: MapBackground | null
  /** Afficher le contrôle de navigation (zoom). Défaut: true. */
  showNavigationControl?: boolean
  /** Afficher la rose des vents (clic = remet le nord en haut). Défaut: false. */
  showCompass?: boolean
  /** Position du contrôle de navigation. Défaut: top-right. */
  navigationControlPosition?: ComponentProps<typeof NavigationControl>['position']
  /** Position de l'attribution. Défaut: bottom-right. */
  attributionPosition?: AttributionPosition
  /** Attribution compacte MapLibre (option native). Défaut: true. */
  attributionCompact?: boolean
  /** Afficher le contrôle d'attribution. Défaut: true. */
  showAttributionControl?: boolean
  /**
   * Limite le pan/zoom à la zone PMTiles (Québec est).
   * Défaut: true. Passer false pour une carte sans contrainte régionale.
   */
  constrainToRegion?: boolean
  children?: ReactNode
}

export const BaseMap = forwardRef<MapRef, BaseMapProps>(function BaseMap(
  {
    mapStyle,
    basemapContext = 'default',
    mapBackground = null,
    showNavigationControl = true,
    showCompass = false,
    navigationControlPosition = 'top-right',
    attributionPosition = 'bottom-right',
    attributionCompact = true,
    showAttributionControl = true,
    constrainToRegion = true,
    maxBounds: maxBoundsProp,
    minZoom: minZoomProp,
    children,
    ...rest
  },
  ref,
) {
  const scheme = useAppColorScheme()
  const resolvedStyle = useMemo(
    () => mapStyle ?? resolveBasemapStyleUrl(basemapContext, scheme, mapBackground),
    [mapStyle, basemapContext, scheme, mapBackground],
  )
  const maxBounds =
    maxBoundsProp ?? (constrainToRegion ? MAP_MAX_BOUNDS : undefined)
  const minZoom = minZoomProp ?? (constrainToRegion ? MAP_MIN_ZOOM : undefined)

  return (
    <MaplibreMap
      ref={ref}
      mapStyle={resolvedStyle}
      maxBounds={maxBounds}
      minZoom={minZoom}
      attributionControl={false}
      {...rest}
    >
      {showAttributionControl && (
        <AttributionControl position={attributionPosition} compact={attributionCompact} />
      )}
      {showAttributionControl && attributionCompact && <CollapseAttributionOnLoad enabled />}
      {showNavigationControl && (
        <NavigationControl
          position={navigationControlPosition}
          showCompass={showCompass}
        />
      )}
      {children}
    </MaplibreMap>
  )
})
